<template>
  <div>
    <c-loading-area v-if="showSummaryData" :loading="loading">
      <v-card-title>
        {{ $t('page.makePayment.summary.title') }}
      </v-card-title>
      <v-card-subtitle>
        {{ $t('page.makePayment.summary.subTitle') }}
      </v-card-subtitle>
      <v-card-text>
        <dl class="d-grid">
          <template v-if="!isSingleCourtMode">
            <dt>{{ $t('page.makePayment.summary.court') }}</dt>
            <dd>{{ caseInstance.courtName }}</dd>
          </template>
          <dt>{{ $t('page.makePayment.summary.caseNumber') }}</dt>
          <dd>{{ caseInstance.caseNumber }}</dd>
          <dt>{{ $t('page.makePayment.summary.caseTitle') }}</dt>
          <dd>{{ caseInstance.caseTitle }}</dd>
          <dt>{{ $t('page.makePayment.summary.caseParty') }}</dt>
          <dd>{{ party.sortName }}</dd>
          <dt>{{ $t('page.makePayment.summary.paymentOption') }}</dt>
          <dd>{{ paymentOption.paymentOptionName }}</dd>
          <dt>{{ $t('page.makePayment.summary.amountOwed') }}</dt>
          <dd>{{ bean.selections.amount | formatCurrency }}</dd>
        </dl>
      </v-card-text>
    </c-loading-area>
    <c-loading-area v-if="supportsPartialPayment && showSummaryData" :loading="loading" class="mt-4">
      <v-card-title>
        {{ $t('page.makePayment.summary.paymentAmount.title') }}
      </v-card-title>
      <v-card-subtitle>
        {{ $t('page.makePayment.summary.paymentAmount.subTitle') }}
      </v-card-subtitle>
      <v-card-text>
        <c-number :label="$t('page.makePayment.summary.paymentAmount')"
                  v-model="paymentAmount"
                  @input="debounceUpdatePreview"
                  :integer="false"
                  key="paymentAmount"
                  required
                  outlined
                  hide-details="auto"
                  aria-describedby="58e8849f-10a1-4a4b-aaa3-ae3d3caca3a3"/>
        <field-description id="58e8849f-10a1-4a4b-aaa3-ae3d3caca3a3" message-key="page.makePayment.summary.paymentAmount.description"/>
      </v-card-text>
    </c-loading-area>
    <c-loading-area v-if="showSummaryData" :loading="loading" class="mt-4">
      <v-card-title>
        {{ $t('page.makePayment.summary.receiptOrderSummary.title') }}
      </v-card-title>
      <v-card-subtitle>
        {{ $t('page.makePayment.summary.receiptOrderSummary.subtitle') }}
      </v-card-subtitle>
      <v-card-text class="px-4">
        <template v-if="!isValidAmount">
          {{ $t('page.makePayment.summary.enterPaymentAmount') }}
        </template>
        <v-progress-linear v-else-if="preview === null" indeterminate class="my-4"/>
        <receipt-order-line-items v-else :receipt="preview"/>
      </v-card-text>
      <v-card-actions class="justify-end">
        <!-- Payment Processor Button -->
        <template v-if="hasValidPaymentProcessorButtonImage">
          <v-btn class="px-0 elevation-1 rounded" @click="save">
            <img :src="paymentProcessorButtonImageLink()" style="height:36px;border-radius:4px;" alt=""/>
            <span class="d-sr-only">
              {{ $t('global.checkout') }}
            </span>
          </v-btn>
        </template>
        <template v-else>
          <!-- Fallback button to be displayed if the payment processor image link cannot be found -->
          <c-btn type="primary" class="px-4" @click="save">
            {{ $t('global.checkout') }}
          </c-btn>
        </template>
      </v-card-actions>
    </c-loading-area>
  </div>
</template>

<script>
import _ from 'lodash';
import axios from 'axios';
import ReceiptOrderLineItems from '@components/support/accounting/ReceiptOrderLineItems';
import WorkflowStepMixin from '@/mixins/workflowStep';
import { scrollTo } from '@/application/utility';
import { useApplicationStore } from '@/stores/applicationStore';

const applicationStore = useApplicationStore();

export default {
  components: { ReceiptOrderLineItems },
  mixins: [ WorkflowStepMixin ],
  data () {
    return {
      preview: null,
      paymentAmount: null,
      previousAmount: null,
      isValidAmount: false,
      showSummaryData: true
    };
  },
  computed: {
    isSingleCourtMode () {
      return applicationStore.isSingleCourtMode;
    },
    hasValidPaymentProcessorButtonImage () {
      return !_.isNil(this.paymentProcessorButtonImageLink());
    },
    caseInstance () {
      return _.find(this.bean.caseSearchResults, ['caseInstanceUUID', this.bean.selections.caseInstanceUUID]);
    },
    party () {
      return _.find(this.bean.partySearchResults, ['casePartyUUID', this.bean.selections.casePartyUUID]);
    },
    paymentOption () {
      return _.find(this.bean.paymentOptionResults, ['paymentOptionID', this.bean.selections.paymentOptionID]);
    },
    supportsPartialPayment () {
      return this.paymentOption.allowOnlinePartialPayment;
    },
    fullPaymentAmount () {
      return this.bean.selections.amount;
    }
  },
  methods: {
    loadData () {
      this.paymentAmount = String(this.fullPaymentAmount);
      return this.updatePreview();
    },
    debounceUpdatePreview: _.debounce(function () { // do not use arrow function here
      this.updatePreview();
    }, 500),
    updatePreview () {

      let amountAsNumber = this.$cove.parseNumber(this.paymentAmount);

      // if the amount entered is NaN or not in a valid range, show the "enter amount" message
      this.isValidAmount = amountAsNumber !== null && amountAsNumber > 0 && amountAsNumber <= this.fullPaymentAmount;

      // don't reload the line items if the amount is invalid (the message will be shown instead) or if they didn't change the value.
      if (this.isValidAmount && amountAsNumber !== this.previousAmount) {

        this.preview = null;
        this.previousAmount = amountAsNumber;

        let previewURL = this.$cove.getAPI({
          name: 'api.casefeepayments.preview',
          query: {
            caseIdentifier: this.bean.selections.caseInstanceUUID,
            casePartyExternalIdentifier: this.bean.selections.casePartyUUID,
            courtID: applicationStore.getCourtByExternalID(this.caseInstance.courtID).resourceID,
            paymentAmount: amountAsNumber,
            paymentOptionExternalIdentifier: this.bean.selections.paymentOptionID
          }
        });
        return axios.get(previewURL)
          .then((response) => {
            this.preview = response.data;
          }).catch((error) => {
            this.showSummaryData = false;
            console.error(error);
          });
      } else {
        return Promise.resolve();
      }
    },
    paymentProcessorButtonImageLink () {
      let paymentClientName = applicationStore.getAppProperty(this.CONST.APP_PROPERTY_PAYMENT_CLIENT);
      try
      {
        return require('@/assets/paymentClient/' + paymentClientName + '/checkout.png');
      }
      catch (e)
      {
        // Catch and eat exception if the image can't be found; A fallback button will be displayed instead
        return null;
      }
    },
    validate () {
      this.$cove.validation.clear();

      this.$cove.validation.required(this.paymentAmount, 'page.makePayment.summary.paymentAmount', 'paymentAmount');
      this.$cove.validation.decimal.rejectIfInvalid(this.paymentAmount, 'page.makePayment.summary.paymentAmount', 'paymentAmount');
      this.$cove.validation.decimal.rejectIfLessThanEqualTo(this.paymentAmount, 0, 'page.makePayment.summary.paymentAmount',
        'paymentAmount');
      this.$cove.validation.decimal.rejectIfGreaterThan(this.paymentAmount, this.fullPaymentAmount,
        'page.makePayment.summary.paymentAmount', 'paymentAmount');

      return !this.$cove.validation.hasErrors();
    },
    save () {

      if (this.supportsPartialPayment && !this.validate())
      {
        scrollTo(this.CONST.ERROR_CONTAINER_SELECTOR);
      }
      else
      {
        this.$cove.confirm({
          message: this.$t('page.makePayment.summary.confirm', [this.$cove.formatCurrency(this.preview.amount)])
        }).then((confirmed) => {
          if (confirmed)
          {
            this.checkoutWithPaymentProcessor();
          }
        });
      }
    },
    checkoutWithPaymentProcessor () {
      this.$cove.blockNow();

      let params = {
        caseIdentifier: this.bean.selections.caseInstanceUUID,
        casePartyExternalIdentifier: this.bean.selections.casePartyUUID,
        courtID: applicationStore.getCourtByExternalID(this.caseInstance.courtID).resourceID,
        paymentAmount: this.paymentAmount,
        paymentOptionExternalIdentifier: this.bean.selections.paymentOptionID,
        previewTotalAmount: this.preview.amount
      };

      axios.post(this.$cove.getAPI({ name: 'api.casefeepayments.checkout' }), params)
        .then((response) => {
          if (_.isNil(response.data.redirectLocation))
          {
            this.$cove.notify({ message: this.$t('page.makePayment.summary.success'), color: 'success' });
            this.$router.push({ name: 'receipt', params: { receiptID: response.data.receiptUUID } });
            this.$cove.unblock();
          }
          else
          {
            // redirect to payment processor
            window.location.href = response.data.redirectLocation;
          }
        })
        .catch(() => {
          this.$cove.unblock();
        });
    }
  }
};
</script>
